import { DateFormat, formatDateTimeWithLocale } from '@frontend/common';
import { DeviceSensorClient } from '@frontend/device/api';
import { Device, DeviceType, SensorLevel, SensorType } from '@frontend/device/types';
import { MqttBroker } from '@frontend/pub-sub';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { QrReaderProps } from './qr-reader.component';

interface ViewProps {
    relevantDevices: Device[];
    selectedDevice: Device | null;
    setSelectedDevice: (device: Device | null) => void;

    qr: string;
    setQR: (qr: string) => void;
    scanCode: () => void;

    messages: { incoming: boolean; message: string; info: string; timestamp: string; data: any }[];
}

const useQrReader = (props: QrReaderProps): ViewProps => {
    const intl = useIntl();
    const subscriberId = `qr-reader-emulator-${props.iot.id}`;
    const relevantDevices = props.devices.filter((device) => device.type === DeviceType.QR_READER);
    const [selectedDevice, setSelectedDevice] = useState<Device | null>(relevantDevices.length > 0 ? relevantDevices[0] : null);
    const [qr, setQR] = useState<string>('');
    const [messages, setMessages] = useState<{ incoming: boolean; message: string; info: string; timestamp: string; data: any }[]>([]);
    const addMessage = (incoming: boolean, message: string, info: string, data: any) => {
        const time = formatDateTimeWithLocale(new Date(), intl.locale, DateFormat.HH_MM_SS_MS);
        setMessages((prev) => [{ incoming, message, info, timestamp: time, data }, ...prev]);
    };

    const onQrCodeScanned = (message: string, data: any) => {
        addMessage(true, message, 'QR code scanned: ' + data.data.qr_code, data);
    };

    const callback = (message: string, data: any) => {
        if (message === 'device_sensor_received' && data?.type === SensorType.QR_CODE) onQrCodeScanned(message, data);
    };

    const scanCode = async () => {
        if (!selectedDevice) return;
        const body = {
            level: SensorLevel.INFO,
            type: SensorType.QR_CODE,
            data: {
                qr_code: qr
            }
        };
        addMessage(false, 'Sent QR code scan', 'QR code: ' + qr, body);
        return DeviceSensorClient.postDeviceSensor(props.iot.account_id, props.iot.id, selectedDevice.id, body, true, props.token).catch((error) => {
            console.error('Failed to post QR code scan sensor data:', error);
        });
    };

    useEffect(() => {
        MqttBroker.getInstance().subscribe(subscriberId, `iots/${props.iot.id}/sensor`, callback);
        return () => {
            MqttBroker.getInstance().unSubscribe(subscriberId, `iots/${props.iot.id}/sensor`);
        };
    }, []);

    return {
        relevantDevices,
        selectedDevice,
        setSelectedDevice,
        qr,
        setQR,
        scanCode,
        messages
    };
};

export default useQrReader;
